import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { ProgressSpinner } from "primereact/progressspinner";

const DepositCreate = () => {
  const navigate = useNavigate();

  // Default form structure
  const getDefaultFormData = () => ({
    bank_id: 0,
    amount: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    counter_no: "",
    status: "active",
    staff_id: null,
  });

  const [depositEntries, setDepositEntries] = useState([getDefaultFormData()]);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [bankBalances, setBankBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState({});
  const [balanceErrors, setBalanceErrors] = useState({});
  const [counterStaff, setCounterStaff] = useState([]);
  const [depositCategories, setDepositCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [staffData, setStaffData] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [vendorData, setVendorData] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [shopData, setShopData] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const shopDataFetched = useRef(false);
  const [departmentData, setDepartmentData] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const departmentDataFetched = useRef(false);
  useEffect(() => {
    fetchBanks();
    fetchDepositCategories();
  }, []);

  // Fetch staff data when needed
  useEffect(() => {
    const needsStaffData = depositEntries.some(entry => 
      [2, 3, 4, 7].includes(entry.category_id)
    );
    
    if (needsStaffData && staffData.length === 0 && !loadingStaff) {
      fetchStaffData();
    }
  }, [depositEntries, staffData.length, loadingStaff]);

  // Fetch vendor data when needed
  useEffect(() => {
    const needsVendorData = depositEntries.some(entry => 
      entry.category_id === 5
    );
    
    if (needsVendorData && vendorData.length === 0 && !loadingVendors) {
      fetchVendorData();
    }
  }, [depositEntries, vendorData.length, loadingVendors]);

  // Fetch shop data when needed
  useEffect(() => {
    const needsShopData = depositEntries.some(entry => 
      entry.category_id === 6
    );
    
    if (needsShopData && !shopDataFetched.current && !loadingShops) {
      fetchShopData();
    }
  }, [depositEntries]);

  // Fetch department data when needed
  useEffect(() => {
    const needsDepartmentData = depositEntries.some(entry => 
      entry.category_id === 1
    );
    
    if (needsDepartmentData && !departmentDataFetched.current && !loadingDepartments) {
      fetchDepartmentData();
    }
  }, [depositEntries]);

  const fetchBanks = () => {
    setLoadingBanks(true);
    Http.get("/accounts/banks")
      .then((res) => {
        setBanks(res.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoadingBanks(false);
      });
    // Fetch counter staff
    Http.get("/counter_staff")
      .then((res) => {
        setCounterStaff(res.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoadingBanks(false);
      });
  };

  const fetchDepositCategories = () => {
    setLoadingCategories(true);
    Http.get("/accounts/deposit-categories")
      .then((res) => {
        setDepositCategories(res.data);
      })
      .catch((error) => {
        console.error("Fetch deposit categories error:", error);
      })
      .finally(() => {
        setLoadingCategories(false);
      });
  };

  const fetchStaffData = () => {
    setLoadingStaff(true);
    Http.get("/staff2")
      .then((res) => {
        setStaffData(res.data);
      })
      .catch((error) => {
        console.error("Fetch staff data error:", error);
      })
      .finally(() => {
        setLoadingStaff(false);
      });
  };

  const fetchVendorData = () => {
    setLoadingVendors(true);
    Http.get("/vendorSelect2")
      .then((res) => {
        setVendorData(res.data);
      })
      .catch((error) => {
        console.error("Fetch vendor data error:", error);
      })
      .finally(() => {
        setLoadingVendors(false);
      });
  };

  const fetchShopData = () => {
    setLoadingShops(true);
    Http.get("/accounts/shops-2")
      .then((res) => {
        setShopData(res.data);
        shopDataFetched.current = true;
      })
      .catch((error) => {
        console.error("Fetch shop data error:", error);
      })
      .finally(() => {
        setLoadingShops(false);
      });
  };

  const fetchDepartmentData = () => {
    setLoadingDepartments(true);
    Http.get("/department2")
      .then((res) => {
        setDepartmentData(res.data);
        departmentDataFetched.current = true;
      })
      .catch((error) => {
        console.error("Fetch department data error:", error);
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
  };

  // Add new deposit entry
  const addDepositEntry = () => {
    setDepositEntries([...depositEntries, getDefaultFormData()]);
  };

  // Remove deposit entry
  const removeDepositEntry = (index) => {
    if (depositEntries.length > 1) {
      const newEntries = depositEntries.filter((_, i) => i !== index);
      setDepositEntries(newEntries);

      // Clean up related state for removed entry
      const newBankBalances = { ...bankBalances };
      const newLoadingBalances = { ...loadingBalances };
      const newBalanceErrors = { ...balanceErrors };

      delete newBankBalances[index];
      delete newLoadingBalances[index];
      delete newBalanceErrors[index];

      setBankBalances(newBankBalances);
      setLoadingBalances(newLoadingBalances);
      setBalanceErrors(newBalanceErrors);
    }
  };

  const fetchBankBalance = (bankId, entryIndex) => {
    if (!bankId) {
      setBankBalances(prev => ({ ...prev, [entryIndex]: null }));
      setBalanceErrors(prev => ({ ...prev, [entryIndex]: "" }));
      return;
    }

    setLoadingBalances(prev => ({ ...prev, [entryIndex]: true }));
    setBalanceErrors(prev => ({ ...prev, [entryIndex]: "" }));

    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalances(prev => ({ ...prev, [entryIndex]: res.data.data }));
        } else {
          setBalanceErrors(prev => ({ ...prev, [entryIndex]: "Failed to fetch bank balance" }));
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceErrors(prev => ({ ...prev, [entryIndex]: "Error fetching bank balance" }));
        setBankBalances(prev => ({ ...prev, [entryIndex]: null }));
      })
      .finally(() => {
        setLoadingBalances(prev => ({ ...prev, [entryIndex]: false }));
      });
  };

  const handleChange = (e, entryIndex) => {
    const name = e.target.name;
    const value = e.target.value;

    const newEntries = [...depositEntries];
    newEntries[entryIndex] = { ...newEntries[entryIndex], [name]: value };
    setDepositEntries(newEntries);

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value, entryIndex);
    }
  };

  const selectHandleChange = (selectedOption, actionMeta, entryIndex) => {
    const name = actionMeta.name;
    const value = selectedOption.value;

    const newEntries = [...depositEntries];
    newEntries[entryIndex] = { ...newEntries[entryIndex], [name]: value };
    setDepositEntries(newEntries);

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value, entryIndex);
    }
  };

  const formSubmit = () => {
    setLoading(true);

    // Validate all entries have required fields
    const validEntries = depositEntries.filter(entry =>
      entry.bank_id && entry.amount && entry.date && entry.category_id
    );

    if (validEntries.length === 0) {
      setError({ general: "Please fill in at least one complete deposit entry" });
      setLoading(false);
      return;
    }

    // Submit all valid entries
    const promises = validEntries.map(entry =>
      Http.post("/accounts/deposits", entry)
    );

    Promise.all(promises)
      .then((responses) => {
        const successCount = responses.length;
        navigate("/deposits", {
          state: {
            message: `${successCount} deposit${successCount > 1 ? 's' : ''} created successfully`,
            type: "success"
          },
        });
      })
      .catch(function (e) {
        setError(e.response?.data?.errors || { general: "Error creating deposits" });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Create Deposit</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Deposit Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>

            {loadingBanks ? (
              <ProgressSpinner />
            ) : (
              <>
                {depositEntries.map((entry, index) => (
                  <div key={index} className="deposit-entry-container mb-4">
                    {depositEntries.length > 1 && (
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 text-primary">
                          <i className="fa fa-money-bill-wave me-2"></i>
                          Deposit Entry #{index + 1}
                        </h6>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeDepositEntry(index)}
                          title="Remove this entry"
                        >
                          <i className="fa fa-trash"></i> Remove
                        </button>
                      </div>
                    )}


                    <div className="card border-light shadow-sm">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg">
                            <label>
                              Deposit Category <span className="text-danger">*</span>
                            </label>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="category_id"
                              value={
                                depositCategories
                                  .map((category) => ({
                                    value: category.id,
                                    label: category.name,
                                  }))
                                  .find((option) => option.value === entry.category_id) ||
                                null
                              }
                              options={depositCategories.map((category) => ({
                                value: category.id,
                                label: category.name,
                              }))}
                              onChange={(selectedOption) => {
                                const newEntries = [...depositEntries];
                                newEntries[index] = {
                                  ...newEntries[index],
                                  category_id: selectedOption.value,
                                  staff_id: null, // Reset staff_id when category changes
                                };
                                setDepositEntries(newEntries);
                              }}
                              placeholder="Select Deposit Category"
                              isLoading={loadingCategories}
                            />
                          </div>
                          
                          {/* Conditional Staff Dropdown */}
                          {[2, 3, 4, 7].includes(entry.category_id) && (
                            <div className="col-lg">
                              <label>
                                Staff List <span className="text-danger">*</span>
                              </label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                name="staff_id"
                                value={
                                  staffData
                                    .map((staff) => ({
                                      value: staff.value,
                                      label: staff.label,
                                    }))
                                    .find((option) => option.value === entry.staff_id) ||
                                  null
                                }
                                options={staffData.map((staff) => ({
                                  value: staff.value,
                                  label: staff.label,
                                }))}
                                onChange={(selectedOption) => {
                                  const newEntries = [...depositEntries];
                                  newEntries[index] = {
                                    ...newEntries[index],
                                    staff_id: selectedOption.value,
                                  };
                                  setDepositEntries(newEntries);
                                }}
                                placeholder="Select Staff"
                                isLoading={loadingStaff}
                              />
                            </div>
                          )}

                          {/* Conditional Department Dropdown */}
                          {entry.category_id === 1 && (
                            <div className="col-lg">
                              <label>
                                Department <span className="text-danger">*</span>
                              </label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                name="department_id"
                                value={
                                  departmentData
                                    .map((department) => ({
                                      value: department.value,
                                      label: department.label,
                                    }))
                                    .find((option) => option.value === entry.department_id) ||
                                  null
                                }
                                options={departmentData.map((department) => ({
                                  value: department.value,
                                  label: department.label,
                                }))}
                                onChange={(selectedOption) => {
                                  const newEntries = [...depositEntries];
                                  newEntries[index] = {
                                    ...newEntries[index],
                                    department_id: selectedOption.value,
                                  };
                                  setDepositEntries(newEntries);
                                }}
                                placeholder="Select Department"
                                isLoading={loadingDepartments}
                              />
                            </div>
                          )}

                          {/* Conditional Shop Dropdown */}
                          {entry.category_id === 6 && (
                            <div className="col-lg">
                              <label>
                                Shop <span className="text-danger">*</span>
                              </label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                name="shop_id"
                                value={
                                  shopData
                                    .map((shop) => ({
                                      value: shop.value,
                                      label: shop.label,
                                    }))
                                    .find((option) => option.value === entry.shop_id) ||
                                  null
                                }
                                options={shopData.map((shop) => ({
                                  value: shop.value,
                                  label: shop.label,
                                }))}
                                onChange={(selectedOption) => {
                                  const newEntries = [...depositEntries];
                                  newEntries[index] = {
                                    ...newEntries[index],
                                    shop_id: selectedOption.value,
                                  };
                                  setDepositEntries(newEntries);
                                }}
                                placeholder="Select Shop"
                                isLoading={loadingShops}
                              />
                            </div>
                          )}
                          
                          {/* Conditional Vendor Dropdown */}
                          {entry.category_id === 5 && (
                            <div className="col-lg">
                              <label>
                                Vendor <span className="text-danger">*</span>
                              </label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                name="vendor_id"
                                value={
                                  vendorData
                                    .map((vendor) => ({
                                      value: vendor.value,
                                      label: vendor.label,
                                    }))
                                    .find((option) => option.value === entry.vendor_id) ||
                                  null
                                }
                                options={vendorData.map((vendor) => ({
                                  value: vendor.value,
                                  label: vendor.label,
                                }))}
                                onChange={(selectedOption) => {
                                  const newEntries = [...depositEntries];
                                  newEntries[index] = {
                                    ...newEntries[index],
                                    vendor_id: selectedOption.value,
                                  };
                                  setDepositEntries(newEntries);
                                }}
                                placeholder="Select Vendor"
                                isLoading={loadingVendors}
                              />
                            </div>
                          )}
                          
                          <div className="col-lg">
                            <label>
                              Amount <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              name="amount"
                              value={entry.amount}
                              onChange={(e) => handleChange(e, index)}
                              placeholder="Enter Amount"
                            />
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-lg">
                            <label>
                              Bank <span className="text-danger">*</span>
                            </label>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="bank_id"
                              value={
                                banks
                                  .map((bank) => ({
                                    value: bank.id,
                                    label: `${bank.name} - ${bank.account_number}`,
                                  }))
                                  .find((option) => option.value === entry.bank_id) ||
                                null
                              }
                              options={banks.map((bank) => ({
                                value: bank.id,
                                label: `${bank.name} - ${bank.account_number}`,
                              }))}
                              onChange={(selectedOption) => {
                                const newEntries = [...depositEntries];
                                newEntries[index] = {
                                  ...newEntries[index],
                                  bank_id: selectedOption.value,
                                };
                                setDepositEntries(newEntries);
                                fetchBankBalance(selectedOption.value, index);
                              }}
                            />
                          </div>

                          <div className="col-lg">
                            <label>
                              Deposit Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              name="date"
                              value={entry.date}
                              onChange={(e) => handleChange(e, index)}
                            />
                          </div>

                        </div>

                        {/* Bank Balance Display */}
                        {entry.bank_id && (
                          <div className="row mt-2">
                            <div className="col-lg-6">
                              <div
                                className="alert alert-info"
                                style={{ marginBottom: "15px" }}
                              >
                                <strong>Bank Balance Information:</strong>
                                {loadingBalances[index] ? (
                                  <div className="d-flex align-items-center mt-2">
                                    <div
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    >
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                    <span>Loading bank balance...</span>
                                  </div>
                                ) : balanceErrors[index] ? (
                                  <div className="text-danger mt-2">
                                    <i className="fa fa-exclamation-triangle me-1"></i>
                                    {balanceErrors[index]}
                                  </div>
                                ) : bankBalances[index] ? (
                                  <div className="mt-2">
                                    <div className="row">
                                      <div className="col-md-4">
                                        <small className="text-muted">Bank Name:</small>
                                        <div className="font-weight-bold">
                                          {bankBalances[index].name}
                                        </div>
                                      </div>
                                      <div className="col-md-4">
                                        <small className="text-muted">
                                          Account Number:
                                        </small>
                                        <div className="font-weight-bold">
                                          {bankBalances[index].account_number}
                                        </div>
                                      </div>
                                      <div className="col-md-4">
                                        <small className="text-muted">
                                          Current Balance:
                                        </small>
                                        <div className="font-weight-bold text-success">
                                          ৳ {bankBalances[index].total_balance}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="row mt-3">

                          {/* <div className="col-lg">
                            <label>Counter No</label>
                            <input
                              type="text"
                              className="form-control"
                              name="counter_no"
                              value={entry.counter_no}
                              onChange={(e) => handleChange(e, index)}
                              placeholder="Enter Counter Number"
                            />
                          </div> */}

                          {![1, 2, 3, 4, 6, 7, 5].includes(entry.category_id) && (
                            <div className="col-lg">
                              <label>
                                Counter Staff
                              </label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                name="counter_id"
                                value={
                                  counterStaff
                                    .map((staff) => ({
                                      value: staff.id,
                                      label: `${staff.name} - ${staff.card_no}`,
                                    }))
                                    .find((option) => option.value === entry.counter_id) ||
                                  null
                                }
                                options={counterStaff
                                  .map((staff) => ({
                                    value: staff.id,
                                    label: `${staff.name} - ${staff.card_no}`,
                                  }))}
                                onChange={(selectedOption) => {
                                  const newEntries = [...depositEntries];
                                  newEntries[index] = {
                                    ...newEntries[index],
                                    counter_id: selectedOption.value,
                                  };
                                  setDepositEntries(newEntries);
                                  // fetchBankBalance(selectedOption.value, index);
                                }}
                                placeholder="Select Counter Staff"
                              />
                            </div>
                          )}
                          
                          <div className="col-lg">
                            <label>Description</label>
                            <textarea
                              className="form-control"
                              name="description"
                              value={entry.description}
                              onChange={(e) => handleChange(e, index)}
                              placeholder="Enter Description"
                              rows="3"
                            ></textarea>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                ))}

                {/* Add More Button */}
                <div className="row mt-3 mb-3">
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-outline-success btn-lg"
                      onClick={addDepositEntry}
                      style={{
                        borderStyle: 'dashed',
                        borderWidth: '2px',
                        width: '100%',
                        padding: '15px'
                      }}
                    >
                      <i className="fa fa-plus-circle me-2"></i>
                      Add More Deposit Entry
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="row mt-3">
                  <div className="col-3">
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <span>
                          <i className="fa fa-spinner fa-spin"></i> Saving...
                        </span>
                      ) : (
                        <span>
                          <i className="fa fa-save mg-r-10"></i>
                          Save {depositEntries.length > 1 ? `All (${depositEntries.length})` : ''}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositCreate;
